import { useConfirmationDialog } from '@openx/components-core/lib/Dialog/ConfirmationDialog/useConfirmationDialog';
import { type JSX, useCallback } from 'react';
import { Trans, useTranslation } from 'react-i18next';

export type BlockingConfirmationParams = {
  onConfirmed: () => void;
  onCancel?: () => void;
  hasManualBundles?: boolean;
  hasInventoryList?: boolean;
};

export type UseBlockingConfirmationResult = {
  showBlockingConfirmation: (params: BlockingConfirmationParams) => void;
};

export const useBlockingConfirmation = (): UseBlockingConfirmationResult => {
  const { t } = useTranslation();
  const confirmationDialog = useConfirmationDialog();

  const buildContent = useCallback((hasManualBundles: boolean, hasInventoryList: boolean): JSX.Element => {
    if (hasManualBundles && hasInventoryList) {
      return (
        <Trans i18nKey="blockingConfirmation.both" components={{ strong: <strong /> }}>
          This action will set Manual App Bundles to <strong>'Block'</strong> and <strong>remove</strong> items from the
          Inventory List, as they are restricted to 'Allow' targeting only.
        </Trans>
      );
    }

    if (hasManualBundles) {
      return (
        <Trans i18nKey="blockingConfirmation.manualBundles" components={{ strong: <strong /> }}>
          This action will set Manual App Bundles to <strong>'Block'</strong>.
        </Trans>
      );
    }

    return (
      <Trans i18nKey="blockingConfirmation.inventoryList" components={{ strong: <strong /> }}>
        This action will <strong>remove</strong> items from the Inventory List, as they are restricted to 'Allow'
        targeting only.
      </Trans>
    );
  }, []);

  const showBlockingConfirmation = useCallback(
    ({ onConfirmed, onCancel, hasManualBundles = false, hasInventoryList = false }: BlockingConfirmationParams) => {
      confirmationDialog.open({
        confirmationButtonColor: 'error',
        confirmationLabel: t('Block And Proceed'),
        content: buildContent(hasManualBundles, hasInventoryList),
        onCancel: () => {
          onCancel?.();
          confirmationDialog.close();
        },
        onConfirmed: () => {
          onConfirmed();
          confirmationDialog.close();
        },
        title: t('Blocking App Name'),
      });
    },
    [confirmationDialog, t, buildContent]
  );

  return { showBlockingConfirmation };
};
